import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "../lib/siteData";

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

const Counter = ({ target, prefix = "", suffix = "", duration = 1800, start }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;
    let raf;
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const v = easeOut(p) * target;
      setValue(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);

  const display = Number.isInteger(target)
    ? Math.round(value).toLocaleString()
    : value.toFixed(1);

  return (
    <span>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export const StatsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      data-testid="stats-section"
      className="bg-[#1A1D20] text-[#FCFCFA] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">Scale & Reach</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
              A regional platform.<br />An African-scale opportunity.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-white/75 text-[15px] md:text-[17px] leading-relaxed">
              The Cape Maclear SEZ sits within a three-hour flight of
              <span className="text-[#D4AF37]"> 23 African nations</span> — a combined
              market of 577 million people and more than $928 billion in annual GDP.
              This is the gateway project of a new regional economy.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, idx) => (
            <motion.div
              key={s.label}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: idx * 0.12 }}
              className={`py-10 md:py-14 px-6 ${
                idx < STATS.length - 1 ? "md:border-r" : ""
              } border-white/10`}
            >
              <div className="font-serif-display text-5xl md:text-6xl text-[#D4AF37] leading-none">
                <Counter
                  target={s.value}
                  prefix={s.prefix || ""}
                  suffix={s.suffix || ""}
                  start={inView}
                />
              </div>
              <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-white/70">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
